import { useEffect, useState } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, MessageCircle, Smartphone, HeartHandshake, CheckCircle2, Circle } from "lucide-react";

const DEFAULT_TPL =
  "Olá {cliente}! Tudo certo com seu {aparelho} após o reparo da OS {numero}? A Alpha Prime agradece a confiança! 💛";

export default function PosVenda() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("pos_sale.manage");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todos"); // todos | pendentes | feitos
  const [loading, setLoading] = useState(true);

  // Dialog de mensagem
  const [waOpen, setWaOpen] = useState(false);
  const [waOs, setWaOs] = useState(null);
  const [waPhone, setWaPhone] = useState("principal");
  const [waMarkDone, setWaMarkDone] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ status: "concluido_retirado" });
      if (q) p.set("q", q);
      const { data } = await api.get(`/service-orders?${p}`);
      setItems(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line

  const shown = items.filter((o) =>
    filter === "todos" ? true : filter === "feitos" ? !!o.posSaleDone : !o.posSaleDone
  );
  const pendingCount = items.filter((o) => !o.posSaleDone).length;

  const toggleDone = async (os) => {
    const done = !os.posSaleDone;
    try {
      const { data } = await api.post(`/service-orders/${os.id}/pos-sale`, { done });
      setItems((arr) => arr.map((x) => (x.id === os.id ? { ...x, ...data } : x)));
      toast.success(done ? "Pós-venda marcado como feito" : "Pós-venda desmarcado");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openWa = (os) => {
    setWaOs(os);
    setWaPhone("principal");
    setWaMarkDone(!os.posSaleDone);
    setWaOpen(true);
  };

  const sendWa = async () => {
    const phone = waPhone === "recado" ? waOs.customerPhone2 : waOs.customerPhone;
    const num = (phone || "").replace(/\D/g, "");
    if (!num) { toast.error("Cliente sem telefone."); return; }
    try {
      const { data } = await api.post(`/service-orders/${waOs.id}/pos-sale`, { done: waMarkDone, notify_phone: num });
      setItems((arr) => arr.map((x) => (x.id === waOs.id ? { ...x, ...data } : x)));
      if (data.whatsapp_sent) {
        toast.success("Mensagem de pós-venda enviada pelo WhatsApp");
      } else {
        const reasons = {
          disabled: "envio automático desativado (Configurações → Mensagens)",
          not_configured: "WhatsApp não configurado no servidor",
          no_phone: "cliente sem telefone",
          send_failed: "falha no envio ao WhatsApp",
          exception: "erro inesperado no envio",
          skipped: "mensagem não enviada",
        };
        toast.error(`Não foi possível enviar: ${reasons[data.whatsapp_reason] || "mensagem não enviada"}`);
      }
      setWaOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const FilterBtn = ({ id, label }) => (
    <button
      data-testid={`pos-filter-${id}`}
      onClick={() => setFilter(id)}
      className={`px-3 h-8 rounded-lg text-sm border transition-colors ${
        filter === id ? "bg-gold/20 border-gold/40 text-gold-bright" : "border-white/10 text-zinc-400 hover:bg-white/5"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6" data-testid="pos-venda-page">
      <div>
        <div className="flex items-center gap-2">
          <HeartHandshake size={22} className="text-gold-bright" />
          <h1 className="text-3xl font-display font-extrabold tracking-tight text-white">Pós-venda</h1>
        </div>
        <p className="text-zinc-400 mt-1">
          Ordens concluídas e retiradas · marque o acompanhamento e envie a mensagem.
          {pendingCount > 0 && <span className="text-gold-bright"> {pendingCount} pendente(s).</span>}
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input data-testid="pos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="OS, cliente ou aparelho..." className="i pl-9" />
        </div>
        <div className="flex gap-2">
          <FilterBtn id="todos" label="Todos" />
          <FilterBtn id="pendentes" label="Pendentes" />
          <FilterBtn id="feitos" label="Feitos" />
        </div>
      </div>

      <div className="grid gap-2">
        {loading ? <p className="text-sm text-zinc-500">Carregando…</p> :
          shown.length === 0 ? <p className="text-sm text-zinc-500" data-testid="pos-empty">Nenhuma OS nesta visão.</p> :
          shown.map((o) => (
            <div key={o.id} data-testid={`pos-row-${o.id}`} className="alpha-card p-4 flex items-center gap-4 flex-wrap">
              <span className="h-10 w-10 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0"><Smartphone size={18} className="text-gold-bright" /></span>
              <div className="flex-1 min-w-0">
                <div className="text-white font-medium truncate flex items-center gap-2">
                  {o.number} · {o.customerName}
                  {o.posSaleDone ? (
                    <Badge data-testid={`pos-badge-${o.id}`} className="bg-green-500/15 text-green-300 border border-green-500/30 hover:bg-green-500/15">Pós-venda feito</Badge>
                  ) : (
                    <Badge data-testid={`pos-badge-${o.id}`} variant="outline" className="border-amber-500/30 text-amber-300">Pendente</Badge>
                  )}
                </div>
                <div className="text-xs text-zinc-500 truncate">
                  {o.device?.brand} {o.device?.model} · {money(o.chargedAmount)} · retirada {o.updatedAt ? new Date(o.updatedAt).toLocaleDateString("pt-BR") : ""}
                  {o.posSaleDone && o.posSaleAt && (
                    <span className="text-green-400/80"> · feito em {new Date(o.posSaleAt).toLocaleDateString("pt-BR")}{o.posSaleByName ? ` por ${o.posSaleByName.split(" ")[0]}` : ""}</span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                {canManage && (
                  <Button
                    data-testid={`pos-toggle-${o.id}`}
                    onClick={() => toggleDone(o)}
                    variant="outline"
                    className={o.posSaleDone
                      ? "border-white/15 bg-transparent text-zinc-300 hover:bg-white/5 h-9"
                      : "border-green-500/30 bg-transparent text-green-300 hover:bg-green-500/10 h-9"}
                  >
                    {o.posSaleDone ? <Circle size={15} className="mr-1.5" /> : <CheckCircle2 size={15} className="mr-1.5" />}
                    {o.posSaleDone ? "Desmarcar" : "Marcar feito"}
                  </Button>
                )}
                <Button data-testid={`pos-wa-${o.id}`} onClick={() => openWa(o)} variant="outline" className="border-green-500/30 bg-transparent text-green-300 hover:bg-green-500/10 h-9">
                  <MessageCircle size={15} className="mr-1.5" />WhatsApp
                </Button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent data-testid="pos-wa-dialog" className="bg-zinc-950 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Mensagem de pós-venda {waOs ? `· OS ${waOs.number}` : ""}</DialogTitle>
          </DialogHeader>
          {waOs && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <button
                  data-testid="pos-wa-num-principal"
                  onClick={() => setWaPhone("principal")}
                  className={`px-3 h-8 rounded-lg text-sm border ${waPhone === "principal" ? "bg-gold/20 border-gold/40 text-gold-bright" : "border-white/10 text-zinc-400"}`}
                >
                  Principal {waOs.customerPhone ? `(${waOs.customerPhone})` : ""}
                </button>
                {waOs.customerPhone2 && (
                  <button
                    data-testid="pos-wa-num-recado"
                    onClick={() => setWaPhone("recado")}
                    className={`px-3 h-8 rounded-lg text-sm border ${waPhone === "recado" ? "bg-gold/20 border-gold/40 text-gold-bright" : "border-white/10 text-zinc-400"}`}
                  >
                    Recado ({waOs.customerPhone2})
                  </button>
                )}
              </div>
              <div className="rounded-lg border border-white/10 bg-zinc-900 p-3 text-sm text-zinc-300" data-testid="pos-wa-message">
                Será enviada a mensagem <strong className="text-white">oficial de pós-venda</strong> (template aprovado pela Meta), com o nome do cliente, o número da OS e o link de avaliação no Google. O texto é fixo e não pode ser editado.
              </div>
              {canManage && !waOs.posSaleDone && (
                <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                  <input data-testid="pos-wa-markdone" type="checkbox" checked={waMarkDone} onChange={(e) => setWaMarkDone(e.target.checked)} className="accent-gold" />
                  Marcar pós-venda como feito ao enviar
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="border-white/15 bg-transparent text-zinc-300" onClick={() => setWaOpen(false)}>Cancelar</Button>
            <Button data-testid="pos-wa-send" onClick={sendWa} className="bg-green-600 hover:bg-green-500 text-white">
              <MessageCircle size={15} className="mr-1.5" />Enviar mensagem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
